import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios, ShortUser } from '@umanforge/common';
import { RoleTask, RoleTaskFull, RoleTaskFullIds, RoleTaskFullPost } from 'interfaces/roleTask.interface';
import { API } from 'constants/api.contants';


export class RoleTaskService {

  static isRoleTaskValid( roleTasks: RoleTaskFull[] ): boolean {
    if ( roleTasks == null || roleTasks.length == 0 ) {
      return false;
    }

    for ( const roleTask of roleTasks ) {
      if ( roleTask.count < roleTask.users.length ) {
        return false;
      }
      if ( !roleTask.isValid ) {
        return false;
      }
    }
    return true;
  }

  static getRoleTasksByTaskId(
    taskId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<RoleTask[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.ROLE_TASK_BY_TASK}/${taskId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<RoleTask[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getRoleTasksFullByTaskId(
    taskId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<RoleTaskFull[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.ROLE_TASK_BY_TASK_FULL}/${taskId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<RoleTaskFull[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const res = response.data.map( ( rt: RoleTaskFull ) => {
            rt.isValid = true;
            return rt;
          } );
          resolve( res );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getRoleTasksFullByTaskIds(
    taskIds: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios
  ): Promise<RoleTaskFullIds[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.ROLE_TASK_BY_TASKS_FULL}`, { ...( await CommonGlobalService.getHeaderRequest( token ) ), params: { taskIds } } )
        .then( ( response: AxiosResponse<RoleTaskFullIds[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            //GlobalService.onAxiosWarning( response, onWarning );
            resolve( [] );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }


  static getUsersByAbilitiesSkillsUsers(
    usersId: UUID[] | undefined,
    abilitiesId: UUID[] | undefined,
    skills: { id: UUID, value: number }[] | undefined,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios
  ): Promise<ShortUser[]> {
    if ( usersId?.length === 0 ) { usersId = undefined; }
    if ( abilitiesId?.length === 0 ) { abilitiesId = undefined; }
    if ( skills?.length === 0 ) { skills = undefined; }
    return new Promise( async resolve => {
      AXIOS.get( `${API.GET_USERS_BY_ABILITIES_SKILLS_USERS}`, { ...( await CommonGlobalService.getHeaderRequest( token ) ), params: { usersId, abilitiesId, skills } } )
        .then( ( response: AxiosResponse<ShortUser[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            resolve( [] );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }


  static deleteRoleTask(
    roleTaskId: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<void> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.ROLE_TASK}/${roleTaskId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve();
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postRoleTaskFull(
    body: RoleTaskFullPost,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.ROLE_TASK, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<string> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postRoleTaskFullByTask(
    taskId: UUID,
    body: RoleTaskFullPost[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( `${API.ROLE_TASK_BY_TASK_FULL}/${taskId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<string> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }


  static fullToFullPost( roleTasksFull: RoleTaskFull[], idTask?: UUID ): RoleTaskFullPost[] {
    const res: RoleTaskFullPost[] = [];
    for ( const roleTaskFull of roleTasksFull ) {
      res.push( {
        count: roleTaskFull.count,
        idTask: idTask ?? roleTaskFull.idTask,
        abilities: roleTaskFull.abilities.map( abl => { return { id: abl.id }; } ),
        skills: roleTaskFull.skills.map( skill => { return { id: skill.id, iluo: skill.iluo }; } ),
        users: roleTaskFull.users.map( user => { return { id: user.id }; } ),
      } );
    }
    return res;
  }

  static fullIdsToFullPost( roleTasksFull: RoleTaskFullIds[], idTask?: UUID ): RoleTaskFullPost[] {
    const res: RoleTaskFullPost[] = [];
    for ( const roleTaskFull of roleTasksFull ) {
      res.push( {
        count: roleTaskFull.count,
        idTask: idTask ?? roleTaskFull.idTask,
        abilities: roleTaskFull.abilities.map( abl => { return { id: abl }; } ),
        skills: roleTaskFull.skills,
        users: roleTaskFull.users.map( user => { return { id: user }; } ),
      } );
    }
    return res;
  }
};